// Canonical Status field options for All Grants & Savings opportunities (Airtable)
// Ordered to match the All Opportunities grant status guide (pipeline → outcomes → internal).

/** Pipeline order — single source of truth for dropdowns and the status guide. */
export const OPPORTUNITY_STATUS_GUIDE_ORDER = [
  // Qualification
  "Value Roadmap",
  "Interested",
  // Drafting & submission
  "In Progress",
  "Ready to Draft",
  "Draft Completed",
  "Sent",
  "Applied - Not Heard Back",
  "Applied - Heard Back",
  // Outcomes
  "Awarded",
  "Lost",
  "Cash Collected",
  "Cancelled",
  "Not Eligible Based on Further Research",
  "Client Has Already Applied For",
  // On hold & internal
  "Paused",
  "Temporarily Closed",
  "Archived",
];

/** Legacy statuses excluded from new selections and filters. */
export const OPPORTUNITY_STATUS_LEGACY_EXCLUDED = [
  "Temporarily Close",
  "Ready for Draft",
  "pending",
  "Suggested",
  "Rejected",
  "rejected",
  "Hidden",
];

/**
 * Legacy spellings that map to a canonical guide status. Used when reading
 * older Airtable records — not shown in dropdowns or filters.
 */
export const OPPORTUNITY_STATUS_LEGACY_ALIAS_GROUPS = [
  ["Ready to Draft", "Ready for Draft"],
  ["Applied - Not Heard Back", "pending"],
  ["Value Roadmap", "Suggested"],
  ["Lost", "Rejected", "rejected"],
  ["Archived", "Hidden"],
];

function normalizeStatusText(status) {
  return String(status || "").trim().toLowerCase();
}

const STATUS_ALIAS_LOOKUP = OPPORTUNITY_STATUS_LEGACY_ALIAS_GROUPS.reduce(
  (lookup, group) => {
    const canonical = group[0];
    group.forEach((alias) => {
      lookup[normalizeStatusText(alias)] = canonical;
    });
    return lookup;
  },
  {}
);

/** Map legacy status spellings to their canonical guide label. */
export function canonicalOpportunityStatus(status) {
  const raw = String(status || "").trim();
  if (!raw) return "";
  return STATUS_ALIAS_LOOKUP[normalizeStatusText(raw)] || raw;
}

/** Include legacy alias spellings when querying Airtable by status. */
export function expandOpportunityStatusAliases(statuses) {
  const expanded = new Set();
  (statuses || []).forEach((status) => {
    const trimmed = String(status || "").trim();
    if (!trimmed) return;
    expanded.add(trimmed);
    const canonical = canonicalOpportunityStatus(trimmed);
    OPPORTUNITY_STATUS_LEGACY_ALIAS_GROUPS.forEach((group) => {
      if (group[0] === canonical) {
        group.forEach((alias) => expanded.add(alias));
      }
    });
  });
  return [...expanded];
}

export function isSelectableOpportunityStatus(status) {
  if (!status) return false;
  if (OPPORTUNITY_STATUS_LEGACY_EXCLUDED.includes(status)) return false;
  return !String(status).toLowerCase().includes("archived");
}

export const OPPORTUNITY_STATUS_OPTIONS = OPPORTUNITY_STATUS_GUIDE_ORDER.map(
  (value) => ({ value, label: value })
);

/**
 * Kanban columns, left to right. "Unspecified" is a blank Status, not an Airtable option.
 */
export const OPPORTUNITY_KANBAN_STAGES = [
  "Awarded",
  "Applied - Not Heard Back",
  "Sent",
  "Draft Completed",
  "Ready to Draft",
  "In Progress",
  "Interested",
  "Value Roadmap",
  "Cancelled",
  "Paused",
  "Not Eligible Based on Further Research",
  "Lost",
  "Unspecified",
  "Archived",
];

export const UNSPECIFIED_KANBAN_STAGE = "Unspecified";

/** Default All Opportunities table filters/sort on page load. */
export const DEFAULT_OPPORTUNITY_STATUS_FILTER = ["In Progress", "Interested"];
export const OPPORTUNITIES_TABLE_STATUS_FILTER_STORAGE_KEY =
  "opportunitiesTable_statusFilter_v2";
export const DEFAULT_OPPORTUNITY_SORT = [
  { field: "AdjustedAmount", direction: "desc" },
  { field: "EstCompletionDate", direction: "asc" },
];

/** Status colors — modal + shared palette (grouped in status dropdown) */
export const OPPORTUNITY_STATUS_COLORS = {
  "Ready to Draft": "#047857",
  "Draft Completed": "#047857",
  "Value Roadmap": "#a16207",
  "In Progress": "#c2410c",
  Interested: "#a16207",
  "Applied - Not Heard Back": "#b45309",
  "Applied - Heard Back": "#0e7490",
  Awarded: "#047857",
  awarded: "#047857",
  "Cash Collected": "#047857",
  Lost: "#be123c",
  "Not Eligible Based on Further Research": "#be123c",
  Cancelled: "#be123c",
  Paused: "#334155",
  "Temporarily Closed": "#334155",
  Archived: "#64748b",
  Sent: "#1d4ed8",
  "Client Has Already Applied For": "#6d28d9",
};

/** Display order for color groups in the status dropdown. */
const STATUS_COLOR_GROUP_ORDER = [
  "#a16207",
  "#6d28d9",
  "#047857",
  "#c2410c",
  "#1d4ed8",
  "#b45309",
  "#0e7490",
  "#be123c",
  "#334155",
  "#64748b",
  "#475569",
];

const STATUS_COLOR_GROUP_LABELS = {
  "#a16207": "Qualification",
  "#6d28d9": "Client interest",
  "#047857": "Active & awarded",
  "#c2410c": "In progress",
  "#1d4ed8": "Sent",
  "#b45309": "Applied — waiting",
  "#0e7490": "Applied — heard back",
  "#be123c": "Lost & closed",
  "#334155": "Paused",
  "#64748b": "Internal",
  "#475569": "Other",
};

export function getOpportunityStatusColor(status) {
  return OPPORTUNITY_STATUS_COLORS[status] ?? "#475569";
}

/** Attach color to each status option for react-select. */
export function mapStatusOptionsWithColors(statusOptions = []) {
  return statusOptions.map((option) => ({
    ...option,
    color: getOpportunityStatusColor(option.value),
  }));
}

/** Group status options by color for react-select (matches grant status guide sections). */
export function mapStatusOptionsGroupedByColor(statusOptions = []) {
  const colored = mapStatusOptionsWithColors(statusOptions);
  const byColor = new Map();

  for (const option of colored) {
    const { color } = option;
    if (!byColor.has(color)) byColor.set(color, []);
    byColor.get(color).push(option);
  }

  const sortIndex = (color) => {
    const index = STATUS_COLOR_GROUP_ORDER.indexOf(color);
    return index === -1 ? STATUS_COLOR_GROUP_ORDER.length : index;
  };

  return [...byColor.entries()]
    .sort(([colorA], [colorB]) => sortIndex(colorA) - sortIndex(colorB))
    .map(([color, options]) => ({
      label: STATUS_COLOR_GROUP_LABELS[color] || "Other",
      color,
      options,
    }));
}

export const OPPORTUNITY_STATUS_VALUES = [...OPPORTUNITY_STATUS_GUIDE_ORDER];

const guideOrderIndex = new Map(
  OPPORTUNITY_STATUS_GUIDE_ORDER.map((status, index) => [status, index])
);

/** Sort status strings in grant-guide order; unknown values sort last alphabetically. */
export function sortByStatusGuideOrder(statuses) {
  return [...new Set(statuses)].sort((a, b) => {
    const indexA = guideOrderIndex.has(a)
      ? guideOrderIndex.get(a)
      : Number.MAX_SAFE_INTEGER;
    const indexB = guideOrderIndex.has(b)
      ? guideOrderIndex.get(b)
      : Number.MAX_SAFE_INTEGER;
    if (indexA !== indexB) return indexA - indexB;
    return String(a).localeCompare(String(b));
  });
}

/** Subset of status options, preserving guide order. */
export function pickStatusOptions(values) {
  const allowed = new Set(values);
  return OPPORTUNITY_STATUS_OPTIONS.filter((option) =>
    allowed.has(option.value)
  );
}

/** Flat status options with colors, excluding legacy/archived entries. */
export function buildSelectableStatusOptions(statuses = OPPORTUNITY_STATUS_GUIDE_ORDER) {
  return mapStatusOptionsWithColors(
    statuses
      .filter(isSelectableOpportunityStatus)
      .map((value) => ({ value, label: value }))
  );
}

/** Grouped status options for filters and dropdowns. */
export function buildGroupedSelectableStatusOptions(
  statuses = OPPORTUNITY_STATUS_GUIDE_ORDER
) {
  return mapStatusOptionsGroupedByColor(buildSelectableStatusOptions(statuses));
}
